import { bindActionCreators } from "@reduxjs/toolkit";
import { connect, useSelector } from "react-redux";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import InfiniteScroll from "react-infinite-scroll-component";
import usePagination from "../../hooks/usePagination";
import productActions from "../../store/actions/items/item.actions";
import { useEffect, useState } from "react";
import ItemCardComponent from "../../components/items/itemCard/ItemCardComponent";
import FilterAndSortProductsComponent from "../../components/items/filterAndSortComponents/FilterAndSortProductsComponent";
import "../../styles/pages/items/itemListPageComponent.scss";
import CategoryNavigationComponent from "../../components/categories/CategoryNavigationComponent";
import useDelayedFlag from "../../hooks/useDelayedFlag";
import { SkeletonCardGrid } from "../../components/general/skeleton";

export const ItemListPageComponent = ({ productActions, categoryReducer }) => {
  const categories = useSelector((state) => state.categoryReducer.categories?.items);
  const navigate = useNavigate();
  const { categoryId, itemGroupId } = useParams();
  const location = useLocation();
  const [categoryDetails, setCategoryDetails] = useState();
  const [itemGroupDetails, setItemGroupDetails] = useState();

  // 0035: this page has no input of its own — the term arrives only through the URL, and as of
  // 0035 the header already debounces the navigation that sets it. The 0022 debounce that used to
  // sit here therefore became a SECOND 350ms wait stacked on the first, and it also delayed a
  // directly-opened /search?query=x link for no reason. The URL value is used as-is.
  //
  // Seeded from the URL rather than starting empty: the old useEffect-only version rendered once
  // with "" and fired a throwaway request for the whole catalogue before the real term arrived.
  const [searchTerm, setSearchTerm] = useState(() => new URLSearchParams(location.search).get("query") || "");

  useEffect(() => {
    setSearchTerm(new URLSearchParams(location.search).get("query") || "");
  }, [location.search]);

  useEffect(() => {
    if (!searchTerm) {
      fetchCategoryAndItemGroup();
    }
  }, [categoryId, itemGroupId, categoryReducer, searchTerm]);

  const fetchCategoryAndItemGroup = () => {
    const selectedCategory = categories?.find((c) => c.id === categoryId);
    setCategoryDetails(selectedCategory);

    const selectedItemGroup = selectedCategory?.itemGroups?.find((g) => g.id === itemGroupId);
    setItemGroupDetails(selectedItemGroup);
  };

  const {
    items: products,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isError,
    totalCount,
  } = usePagination({
    queryKey: ["categoryId", categoryId, "itemGroupId", itemGroupId, "searchTerm", searchTerm],
    fetchFunction: async (pageParam) => {
      const params = {
        pageNumber: pageParam,
        categoryId,
        itemGroupId,
        SearchTerm: searchTerm, // 0035: already settled by the header's debounce
      };
      const response = await productActions.getItems(params);
      return response; // Ensure this matches the expected format
    },
  });

  // 0018: skeleton card grid on the INITIAL load only. usePagination.isLoading is false once
  // the page is cached (default 60s staleTime), and the ~120ms gate means a fast/cached open
  // shows products with no skeleton flash. The InfiniteScroll "load more" loader is untouched.
  const showSkeleton = useDelayedFlag(isLoading);

  const getProductCardElement = (product) => {
    const productCardProps = { product };
    return <ItemCardComponent key={product.id} props={productCardProps} />;
  };

  const getNoItemsElement = () => (
    <div className="noItemsContainer">
      <h2>לא נמצאו פריטים</h2>
      <p>נסה לשנות את הקריטריונים לחיפוש או חזור לעמוד הראשי.</p>
      <button onClick={() => navigate("/")}>חזור לעמוד הראשי</button>
    </div>
  );

  const getProductListEelement = () => {
    return (
      <InfiniteScroll
        dataLength={products.length}
        next={fetchNextPage}
        hasMore={hasNextPage} // Prevent InfiniteScroll while forcing viewport fill
        scrollableTarget="productsListContainer"
        loader={<div>טוען פריטים נוספים...</div>}>
        <div className="productListContainer">{products.map((product) => getProductCardElement(product))}</div>
      </InfiniteScroll>
    );
  };

  return (
    <div className="productsListContainer">
      {/* Display dynamic title */}
      <div className="pageTitleContainer">
        {searchTerm ? (
          <h1>תוצאות חיפוש עבור: {searchTerm}</h1>
        ) : (
          <h1>
            {categoryDetails?.categoryName}
            {itemGroupDetails && ` - ${itemGroupDetails.itemGroupName}`}
          </h1>
        )}
      </div>
      <FilterAndSortProductsComponent totalCount={totalCount} />
      {/* 0018: while the first page is loading, show a skeleton grid (or nothing for the first
          ~120ms) — never the "no items" message, which previously flashed during every load. */}
      {isLoading
        ? showSkeleton && <SkeletonCardGrid count={8} />
        : products && products.length > 0
        ? getProductListEelement()
        : getNoItemsElement()}

      <CategoryNavigationComponent props={{ categoryId, itemGroupId }} />
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    categoryReducer: state.categoryReducer,
  };
};

const mapDispatchToProps = (dispatch) => ({
  productActions: bindActionCreators(productActions, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(ItemListPageComponent);
